/**
 * `/admin/users` — system admin's view of every user with inline credit /
 * invite grant controls.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminUserRow } from '@eloquor/api-contract';
import {
  deleteUser,
  grantCredits,
  grantCreditsEveryone,
  grantInvites,
  grantInvitesEveryone,
  listUsers,
  suspendUser,
  unsuspendUser,
} from '../../api/admin';
import { ApiCallError } from '../../api/client';

export function AdminUsersPage() {
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const { users, total: t } = await listUsers();
      setRows(users);
      setTotal(t);
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not load.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleGrantCredits(id: string, delta: number) {
    // prompt() returns null when the user clicks Cancel. The previous
    // `?? 'admin grant'` defaulted on cancel and still performed the grant,
    // which is the opposite of what Cancel should mean.
    const reason = prompt(`Reason for ${delta} credit grant?`);
    if (reason === null || reason.trim().length === 0) return;
    try {
      await grantCredits(id, { delta, reason });
      setSuccess(`Granted ${delta} credits.`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not grant credits.');
    }
  }
  async function handleGrantInvites(id: string, delta: number, displayName: string) {
    if (!window.confirm(`Grant ${delta} invite${delta === 1 ? '' : 's'} to ${displayName}?`)) return;
    try {
      await grantInvites(id, { delta });
      setSuccess(`Granted ${delta} invite${delta === 1 ? '' : 's'}.`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not grant invites.');
    }
  }
  async function handleGrantEveryoneCredits() {
    const raw = prompt('Credits to grant to every user:');
    if (raw === null) return;
    const delta = Number.parseInt(raw, 10);
    if (!Number.isFinite(delta) || delta === 0) return;
    const reason = prompt('Reason?');
    if (reason === null || reason.trim().length === 0) return;
    try {
      await grantCreditsEveryone({ delta, reason });
      setSuccess(`Granted ${delta} credits to ${total} users.`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not grant credits.');
    }
  }
  async function handleSuspend(id: string, displayName: string, isSuspended: boolean) {
    const verb = isSuspended ? 'unsuspend' : 'suspend';
    if (!window.confirm(`${verb[0]!.toUpperCase() + verb.slice(1)} ${displayName}? ${isSuspended ? 'They will be able to sign in again.' : 'Active sessions will be revoked.'}`)) return;
    try {
      if (isSuspended) await unsuspendUser(id);
      else await suspendUser(id);
      setSuccess(`${displayName} ${isSuspended ? 'unsuspended' : 'suspended'}.`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : `Could not ${verb}.`);
    }
  }
  async function handleDelete(id: string, displayName: string, email: string) {
    if (!window.confirm(`PERMANENTLY DELETE ${displayName} <${email}>? This wipes all of their data and cannot be undone.`)) return;
    try {
      await deleteUser(id);
      setSuccess(`${displayName} deleted.`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not delete.');
    }
  }
  async function handleGrantEveryoneInvites() {
    const raw = prompt('Invites to grant to every user:');
    if (raw === null) return;
    const delta = Number.parseInt(raw, 10);
    if (!Number.isFinite(delta) || delta === 0) return;
    try {
      await grantInvitesEveryone({ delta });
      setSuccess(`Granted ${delta} invites to ${total} users.`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.apiError.message : 'Could not grant invites.');
    }
  }

  return (
    <>
      <div className="auth-eyebrow"><span className="num">ADMIN</span> · Users</div>
      <h1 style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 32, margin: '4px 0 16px' }}>
        Users ({total})
      </h1>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className="btn-ghost" type="button" onClick={handleGrantEveryoneCredits}>
          Grant credits to everyone
        </button>
        <button className="btn-ghost" type="button" onClick={handleGrantEveryoneInvites}>
          Grant invites to everyone
        </button>
      </div>
      {error && <div className="banner danger">{error}</div>}
      {success && <div className="banner">{success}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--fg-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            <th style={{ padding: '8px 12px' }}>User</th>
            <th style={{ padding: '8px 12px' }}>Role</th>
            <th style={{ padding: '8px 12px' }}>Credits</th>
            <th style={{ padding: '8px 12px' }}>Invites</th>
            <th style={{ padding: '8px 12px' }}>Tokens 24h / 7d</th>
            <th style={{ padding: '8px 12px' }}>Last seen</th>
            <th style={{ padding: '8px 12px' }}>Grant</th>
            <th style={{ padding: '8px 12px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isSuspended = Boolean(r.user.suspendedAt);
            return (
            <tr key={r.user.id} style={{ borderTop: '1px solid var(--border)', opacity: isSuspended ? 0.55 : 1 }}>
              <td style={{ padding: '10px 12px' }}>
                <Link
                  to={`/admin/users/${encodeURIComponent(r.user.id)}`}
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  <div style={{ fontWeight: 500 }}>
                    {r.user.displayName}
                    {isSuspended && (
                      <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', border: '1px solid var(--border)', borderRadius: 3, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        suspended
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{r.user.email}</div>
                </Link>
              </td>
              <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)' }}>{r.user.role}</td>
              <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)' }}>
                {r.credits.available} <span style={{ color: 'var(--fg-3)' }}>/ {r.credits.weeklyCardGrant}</span>
              </td>
              <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)' }}>
                {r.invites.available} <span style={{ color: 'var(--fg-3)' }}>({r.invites.lifetimeGranted} ever)</span>
              </td>
              <td style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)' }}>
                {r.usage.tokensLast24h.toLocaleString()} / {r.usage.tokensLast7d.toLocaleString()}
              </td>
              <td style={{ padding: '10px 12px', color: 'var(--fg-3)' }}>
                {r.lastSeenAt ? new Date(r.lastSeenAt).toLocaleDateString() : '—'}
              </td>
              <td style={{ padding: '10px 12px', display: 'flex', gap: 6 }}>
                <button className="btn-ghost" style={{ padding: '4px 8px', fontSize: 12 }} onClick={() => handleGrantCredits(r.user.id, 100)}>
                  +100 cards
                </button>
                <button className="btn-ghost" style={{ padding: '4px 8px', fontSize: 12 }} onClick={() => handleGrantInvites(r.user.id, 1, r.user.displayName)}>
                  +1 invite
                </button>
              </td>
              <td style={{ padding: '10px 12px' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="btn-ghost"
                    style={{ padding: '4px 8px', fontSize: 12 }}
                    onClick={() => handleSuspend(r.user.id, r.user.displayName, isSuspended)}
                  >
                    {isSuspended ? 'Unsuspend' : 'Suspend'}
                  </button>
                  <button
                    className="btn-ghost"
                    style={{ padding: '4px 8px', fontSize: 12, color: 'var(--danger, #c0392b)' }}
                    onClick={() => handleDelete(r.user.id, r.user.displayName, r.user.email)}
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}
